// UploadResearchData.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { createLawImplementationEvidence, createLawImplementationEvidenceCollectors, createLawImplementationProsecutorAssignment, getAuditLawImplementation, getLawImplementation, updateAuditLawImplementation, updateLawImplementation } from "services/lawImplementationService";
import { createDataCollectionEntry, updateLegalResearchRequest } from "services/legalResearchService";
import { LAW_IMPLEMENTATION_REQUEST_STATUS, LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface uploadResearchDataProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
}

const UploadResearchData: React.FC<uploadResearchDataProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onClose,
}) => {
    const { t } = useTranslation();
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [dataSummary, setDataSummary] = useState("");
    const [dataFile, setDataFile] = useState();
    const [dataCollector, setDataCollector] = useState("");
    const [dataCollectorTeam, setDataCollectorTeam] = useState<{ id: string; full_name: string }[]>([]);

    // Create a ref for the modal content div
    const modalContentRef = useRef<HTMLDivElement>(null);

    const handleDataCollectors = (value: string) => {
        setDataCollector(value);
    };
    
    const getInitials = (str: string) =>
        str.split(" ").map(s => s[0]).join("").toUpperCase();

    const handleAddCollector = () => {
        if (dataCollector.trim() !== "") {
            setDataCollectorTeam((prev) => [
                ...prev,
                { id: Date.now().toString(), full_name: dataCollector.trim() }
            ]);
            setDataCollector("");
        }
    };

    const handleRemoveCollector = (id: string) => {
        setDataCollectorTeam((prev) => prev.filter((member) => member.id !== id));
    };

    // Effect to handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);
        setSubmitting(true);

        try {
            const data = new FormData();
            data.append("audit_folloup", request.id.toString());
            data.append("summary_of_evidence", dataSummary);
            if (dataFile)
                data.append("evidence_files", dataFile);
            const evidenceResponse = await createLawImplementationEvidence(data);

            for (const member of dataCollectorTeam) {
                const prosecutorData = new FormData();
                prosecutorData.append("evidence", evidenceResponse.id.toString());
                prosecutorData.append("name", member.full_name);
                await createLawImplementationEvidenceCollectors(prosecutorData);
            }
            const formData = new FormData();
            const response = await updateAuditLawImplementation(request.id.toString(), formData);
            setDataSummary("");
            setDataFile(undefined);
            setDataCollector("");
            setDataCollectorTeam([]);

            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("EvidenceCollectionTeamSubmitted") || "Evidence Collection team Submitted!", "success");
            showToast(t("EvidenceDataUploaded") || "Evidence Data Uploaded", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadEvidenceData") || "Failed to upload evidence data.", "error");
        } finally {
            setSubmitting(false);
        }

        onClose();
    };

    const handleFileChange = (
        file: any,
    ) => {
        setDataFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("UploadEvidence") || "Upload Evidence"}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        {/* Evidence Summary Section */}
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("EvidenceSummary") || "Evidence Summary"}
                            </label>
                            <textarea
                                id="data_summary"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere") || "detail here..."}
                                value={dataSummary}
                                onChange={(e) => setDataSummary(e.target.value)}
                            ></textarea>
                        </div>

                        {/* Collected Evidence File Upload */}
                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("CollectedEvidence") || "Collected Evidence"}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                        
                        {/* Evidence Collectors Section */}
                        <div className="mb-6">
                            <label
                                htmlFor="select-prosecutor"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("EvidenceCollectors") || "Evidence Collectors"}
                            </label>
                            <div className="relative mb-3">
                                <input
                                    className="bg-gray-50 py-3 w-full px-2 outline-none border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                                    placeholder={t("CollectorNamePlaceholder") || "Enter collector name..."}
                                    type="text"
                                    value={dataCollector}
                                    onChange={(e) => handleDataCollectors(e.target.value)}
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleAddCollector}
                                className="mb-4 text-[#AA232F] hover:text-blue-800 text-sm"
                            >
                                {t("AddEvidenceCollector") || "Add Evidence Collector"}
                            </button>

                            <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar scrollbar-hide">
                                {dataCollectorTeam?.length === 0 && (
                                    <p className="text-gray-500 text-sm italic">
                                        {t("NoCollectorsAddedYet") || "No evidence collectors added yet. Please add at least one."}
                                    </p>
                                )}
                                {dataCollectorTeam?.map((member) => (
                                    <div
                                        key={member.id}
                                        className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm"
                                    >
                                        <div className="w-10 h-10 rounded-full mr-3 bg-gray-700 text-white flex items-center justify-center font-bold text-sm select-none">
                                            {getInitials(member.full_name)}
                                        </div>

                                        <div className="flex-grow">
                                            <p className="font-semibold text-gray-800">
                                                {member.full_name}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCollector(member.id)}
                                                className="text-gray-400 hover:text-red-500"
                                                title={t("RemoveCollector") || "Remove collector"}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-5 w-5"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="#AA232F"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md disabled:opacity-70"
                        >
                            {submitting ? (
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    {t("Submitting") || "Submitting..."}
                                </div>
                            ) : (
                                t("Submit") || "Submit"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadResearchData;